import React, { useEffect, useState } from 'react';
import lodashGet from 'lodash/get';
import { Loader } from '../../ui/loader';
import { connect, ConnectedProps } from 'react-redux';

import * as actions from '../../../actions';
import { ARTICLE_PUSHES_LIMIT } from '../../../constants';
import { selectArticleInfoResults } from '../../../selectors';
import { RootState } from '../../../types/redux';

// Code splitting
import CustomInfiniteScroll from '../../common/CustomInfiniteScroll';
import Gallery from '../../common/Gallery';
import Info from './Info';
import Pushes from './Pushes';
import Replies from './Replies';

interface OwnProps {
  id: string;
  authed: boolean;
}

const mapStateToProps = (state: RootState) => ({
  info: selectArticleInfoResults(state),
  isFetching: state.article.info.isFetching,
  isMobile: state.browser.lessThan.medium,
  images: state.article.entities?.images || {},
  pushes: state.article.pushes,
  replies: state.article.replies,
});

const connector = connect(mapStateToProps, actions);
type PropsFromRedux = ConnectedProps<typeof connector>;
type Props = PropsFromRedux & OwnProps;

const Article: React.FC<Props> = ({
  id,
  authed,
  info,
  isFetching,
  isMobile,
  pushes,
  replies,
  images,
  getArticleInfo,
  getArticlePushes,
  getArticlePushesStats,
  getArticleReplies,
}) => {
  const [offset, setOffset] = useState(0);

  const fetchData = () => {
    getArticleInfo(id);
    getArticlePushes(id, ARTICLE_PUSHES_LIMIT, 0);
    getArticlePushesStats(id);
    getArticleReplies(id);
    setOffset(0);
  };

  useEffect(() => {
    if (id !== lodashGet(info, 'id')) {
      fetchData();
    }
  }, [id]);

  const hasMorePushes = ({ data, total }: { data: any; total: number }) => {
    if (!Array.isArray(data) || !data.length) {
      return false;
    }
    return data.length < total;
  };

  const fetchMorePushes = () => {
    if (pushes.isFetching) {
      return;
    }
    getArticlePushes(id, ARTICLE_PUSHES_LIMIT, offset + ARTICLE_PUSHES_LIMIT);
    setOffset(offset + ARTICLE_PUSHES_LIMIT);
  };

  if (isFetching) {
    return (
      <div className="flex justify-center py-8">
        <Loader />
      </div>
    );
  }

  if (!info) {
    return (
      <h1 className="text-3xl text-center text-gray-500">
        Oops..沒有找到這篇文章喔
      </h1>
    );
  }

  return (
    <>
      <Info
        data={{
          active: info.active,
          authorId: info.author_id,
          board: info.board,
          city: info.city,
          code2: info.code2,
          content: info.content,
          country: info.country,
          datetime: info.datetime,
          id: info.id,
          isHidden: info.is_hidden,
          title: info.title,
          updatedAt: info.updated_at,
          url: info.url,
        }}
        replyTo={replies.data && (replies.data as any).reply_to}
        pushesStats={pushes.stats}
        authed={authed}
        isMobile={isMobile}
      />
      <Replies
        data={{ replies: replies.data as any[] }}
        loading={replies.isFetching}
        isMobile={isMobile}
      />
      <Gallery data={info.content} />
      <CustomInfiniteScroll
        dataLength={Array.isArray(pushes.data) ? pushes.data.length : 0}
        fetchMore={fetchMorePushes}
        hasMore={hasMorePushes({
          data: pushes.data,
          total: pushes.total || 0,
        })}
      >
        <Pushes
          data={pushes.data}
          total={pushes.total || 0}
          isMobile={isMobile}
        />
      </CustomInfiniteScroll>
    </>
  );
};

// @ts-ignore - TypeScript issue with React 18 and react-redux types
export default connector(Article) as any;